import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { venueAPI } from '../lib/api';
import { 
  Building2, 
  Plus, 
  MapPin, 
  Pencil,
  Layers,
  Search,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errorMessage';

const buildVenuePayload = (formData) => ({
  ...formData,
  latitude: formData.latitude === '' || formData.latitude === null ? null : parseFloat(formData.latitude),
  longitude: formData.longitude === '' || formData.longitude === null ? null : parseFloat(formData.longitude),
  geofence_radius_meters: parseInt(formData.geofence_radius_meters, 10),
});

const VenueFormFields = ({ formData, setFormData, includeLocationButton = true }) => {
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6),
          }));
          toast.success('Location captured');
        },
        () => {
          toast.error('Failed to get location');
        }
      );
    }
  };

  return (
    <>
      <div>
        <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Venue Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="The Golden Lion"
          required
          className="input-rtb mt-2"
          data-testid="venue-name-input"
        />
      </div>
      <div>
        <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Address *</Label>
        <Input
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="123 High Street, London"
          required
          className="input-rtb mt-2"
          data-testid="venue-address-input"
        />
      </div>
      <div>
        <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Logo URL</Label>
        <Input
          value={formData.logo_url || ''}
          onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
          placeholder="https://.../brand-logo.png"
          className="input-rtb mt-2"
          data-testid="venue-logo-url-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Latitude</Label>
          <Input
            type="number"
            step="any"
            value={formData.latitude}
            onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
            placeholder="51.5074"
            className="input-rtb mt-2"
            data-testid="venue-lat-input"
          />
        </div>
        <div>
          <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Longitude</Label>
          <Input
            type="number"
            step="any"
            value={formData.longitude}
            onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
            placeholder="-0.1278"
            className="input-rtb mt-2"
            data-testid="venue-lng-input"
          />
        </div>
      </div>
      {includeLocationButton && (
        <Button 
          type="button" 
          onClick={getCurrentLocation}
          className="btn-rtb-secondary w-full"
          data-testid="get-location-btn"
        >
          <MapPin className="w-4 h-4 mr-2" />
          Use Current Location
        </Button>
      )}
      <div>
        <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Geofence Radius (meters)</Label>
        <Input
          type="number"
          value={formData.geofence_radius_meters}
          onChange={(e) => setFormData({ ...formData, geofence_radius_meters: e.target.value })}
          placeholder="100"
          className="input-rtb mt-2"
          data-testid="venue-radius-input"
        />
      </div>
    </>
  );
};

const VenueCard = ({ venue, onEdit, canEdit, canManageDepts }) => {
  const navigate = useNavigate();
  const getScoreColor = (score) => {
    if (score >= 90) return { text: 'text-[#00E676]', bg: 'bg-[#00E676]' };
    if (score >= 70) return { text: 'text-[#FFB300]', bg: 'bg-[#FFB300]' };
    return { text: 'text-[#FF3B30]', bg: 'bg-[#FF3B30]' };
  };

  const colors = getScoreColor(venue.compliance_score);

  return (
    <div className="card-rtb group" data-testid={`venue-card-${venue.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="p-3 rounded-md bg-[#27272A]">
          {venue.logo_url ? (
            <img src={venue.logo_url} alt={`${venue.name} logo`} className="w-6 h-6 object-contain" />
          ) : (
            <Building2 className="w-6 h-6 text-[#FF6600]" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1 rounded-full ${colors.bg}/20 ${colors.text} text-sm font-bold`}>
            {venue.compliance_score}%
          </div>
          <div className="flex gap-2">
            {canManageDepts && (
              <Button
                type="button"
                onClick={() => navigate(`/departments/${venue.id}`)}
                className="btn-rtb-secondary px-3"
                title="Manage Departments"
                data-testid={`depts-venue-${venue.id}`}
              >
                <Layers className="w-4 h-4" />
              </Button>
            )}
            {canEdit && (
              <Button
                type="button"
                onClick={() => onEdit(venue)}
                className="btn-rtb-secondary px-3"
                title="Edit Venue"
                data-testid={`edit-venue-${venue.id}`}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
      
      <h3 className="font-heading text-xl font-bold text-white uppercase tracking-tight mb-2">
        {venue.name}
      </h3>
      
      <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-4">
        <MapPin className="w-4 h-4" />
        <span className="truncate">{venue.address}</span>
      </div>
      
      <div className="flex items-center justify-between pt-4 border-t border-[#3F3F46]">
        <span className="text-[#52525B] text-xs uppercase">Compliance Score</span>
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 bg-[#27272A] rounded-full overflow-hidden">
            <div 
              className={`h-full ${colors.bg} transition-all duration-500`}
              style={{ width: `${venue.compliance_score}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateVenueDialog = ({ onVenueCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    logo_url: '',
    latitude: '',
    longitude: '',
    geofence_radius_meters: 100,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = buildVenuePayload(formData);
      
      await venueAPI.create(payload);
      toast.success('Venue created successfully');
      setOpen(false);
      setFormData({ name: '', address: '', logo_url: '', latitude: '', longitude: '', geofence_radius_meters: 100 });
      onVenueCreated();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create venue'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-rtb-primary" data-testid="create-venue-btn">
          <Plus className="w-5 h-5 mr-2" />
          Add Venue
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#1E1E1E] border-[#3F3F46] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase">Create New Venue</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <VenueFormFields formData={formData} setFormData={setFormData} />
          <Button 
            type="submit" 
            disabled={loading}
            className="btn-rtb-primary w-full"
            data-testid="submit-venue-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Venue'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditVenueDialog = ({ venue, open, onOpenChange, onVenueUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: venue?.name || '',
    address: venue?.address || '',
    logo_url: venue?.logo_url || '',
    latitude: venue?.latitude ?? '',
    longitude: venue?.longitude ?? '',
    geofence_radius_meters: venue?.geofence_radius_meters ?? 100,
  });

  useEffect(() => {
    setFormData({
      name: venue?.name || '',
      address: venue?.address || '',
      logo_url: venue?.logo_url || '',
      latitude: venue?.latitude ?? '',
      longitude: venue?.longitude ?? '',
      geofence_radius_meters: venue?.geofence_radius_meters ?? 100,
    });
  }, [venue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!venue) return;
    setLoading(true);

    try {
      await venueAPI.update(venue.id, buildVenuePayload(formData));
      toast.success('Venue updated successfully');
      onOpenChange(false);
      onVenueUpdated();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update venue'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E1E1E] border-[#3F3F46] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase">Edit Venue Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <VenueFormFields formData={formData} setFormData={setFormData} />
          <Button 
            type="submit" 
            disabled={loading}
            className="btn-rtb-primary w-full"
            data-testid="submit-venue-update-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Venue Profile'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const VenuesPage = () => {
  const { user } = useAuthStore();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingVenue, setEditingVenue] = useState(null);

  const fetchVenues = async () => {
    try {
      const response = await venueAPI.list();
      setVenues(response.data);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
      toast.error('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVenues();
  }, []);

  const filteredVenues = venues.filter(venue => 
    venue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    venue.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canEditVenues = hasMinimumRole(user, ROLES.OPS);
  const canManageDepts = hasMinimumRole(user, ROLES.GM);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="venues-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
            Venues
          </h1>
          <p className="text-[#A1A1AA] mt-1">
            Manage your hospitality locations
          </p>
        </div>
        
        {hasMinimumRole(user, ROLES.OPS) && (
          <CreateVenueDialog onVenueCreated={fetchVenues} />
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search venues..."
          className="input-rtb pl-10"
          data-testid="search-venues-input"
        />
      </div>

      {/* Venues Grid */}
      {filteredVenues.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVenues.map((venue) => (
            <VenueCard
              key={venue.id}
              venue={venue}
              canEdit={canEditVenues}
              canManageDepts={canManageDepts}
              onEdit={setEditingVenue}
            />
          ))}
        </div>
      ) : (
        <div className="card-rtb text-center py-12">
          <AlertCircle className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
            {searchQuery ? 'No Venues Found' : 'No Venues Yet'}
          </h3>
          <p className="text-[#A1A1AA] mb-6">
            {searchQuery ? 'Try a different search term' : 'Create your first venue to get started'}
          </p>
          {!searchQuery && hasMinimumRole(user, ROLES.OPS) && (
            <CreateVenueDialog onVenueCreated={fetchVenues} />
          )}
        </div>
      )}

      <EditVenueDialog
        venue={editingVenue}
        open={!!editingVenue}
        onOpenChange={(open) => {
          if (!open) setEditingVenue(null);
        }}
        onVenueUpdated={fetchVenues}
      />
    </div>
  );
};

export default VenuesPage;
